import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Loader2 } from 'lucide-react';

interface DocumentPreviewOverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  // PDF download
  onDownloadPdf?: () => Promise<Blob>;
  pdfFilename?: string;
}

export function DocumentPreviewOverlay({
  open,
  onClose,
  children,
  onDownloadPdf,
  pdfFilename,
}: DocumentPreviewOverlayProps) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  if (!open) return null;

  const handleDownloadPdf = async () => {
    if (!onDownloadPdf) return;

    setDownloadingPdf(true);
    try {
      const blob = await onDownloadPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-gray-100 overflow-auto">
      {/* Close button */}
      <Button
        variant="outline"
        size="icon"
        className="fixed top-4 right-4 z-50 bg-white shadow-lg hover:bg-gray-100 rounded-full"
        onClick={onClose}
      >
        <X className="h-5 w-5" />
      </Button>

      {/* Download PDF button */}
      {onDownloadPdf && (
        <Button
          variant="outline"
          size="sm"
          className="fixed top-4 right-16 z-50 bg-white shadow-lg hover:bg-gray-100"
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
        >
          {downloadingPdf ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {downloadingPdf ? 'Generating...' : 'Download PDF'}
        </Button>
      )}

      {/* Document content - same layout as edit preview */}
      <div className="min-h-screen py-8 px-4 flex justify-center">
        <div className="w-full max-w-[816px]">
          {children}
        </div>
      </div>
    </div>
  );
}
